import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight } from "lucide-react";

/**
 * Slim floating "Request a Demo" pill - mobile-only.
 *
 * Behavior:
 *  - Visible only on `<lg` (mobile + tablet).
 *  - Hidden on /contact, /admin/*, /admin (so we don't double-CTA the form page).
 *  - Hides until user scrolls past ~`window.innerHeight * 0.6` (i.e. past the hero CTA).
 *  - Re-shows whenever scrolling up at the top, hides when at the absolute footer (so it
 *    doesn't overlap the bottom links).
 */
export default function MobileDemoPill() {
  const [visible, setVisible] = useState(false);
  const { pathname } = useLocation();

  const isAdmin = pathname.startsWith("/admin");
  const isContact = pathname === "/contact";

  useEffect(() => {
    if (isAdmin || isContact) {
      setVisible(false);
      return undefined;
    }

    const handler = () => {
      const y = window.scrollY;
      const vh = window.innerHeight;
      const docHeight = document.documentElement.scrollHeight;
      const nearBottom = y + vh > docHeight - 220; // close enough to the footer

      // Prefer measuring the actual hero element so we don't collide with tall
      // hero variants (e.g. hero + featured card layouts). Fall back to a
      // viewport-height heuristic if the hero isn't on the current page.
      const heroEl = document.querySelector("[data-testid='hero-section']");
      let passedHero;
      if (heroEl) {
        const bottom = heroEl.getBoundingClientRect().bottom;
        // Hero must be mostly out of view before the pill appears (16px buffer).
        passedHero = bottom < 16;
      } else {
        passedHero = y > vh * 0.6;
      }
      setVisible(passedHero && !nearBottom);
    };
    handler();
    window.addEventListener("scroll", handler, { passive: true });
    window.addEventListener("resize", handler);
    return () => {
      window.removeEventListener("scroll", handler);
      window.removeEventListener("resize", handler);
    };
  }, [pathname, isAdmin, isContact]);

  if (isAdmin || isContact) return null;

  return (
    <div
      aria-hidden={!visible}
      data-testid="mobile-demo-pill"
      className={`lg:hidden fixed left-1/2 -translate-x-1/2 z-40 transition-all duration-300 ${
        visible
          ? "bottom-[100px] opacity-100 pointer-events-auto translate-y-0"
          : "bottom-[60px] opacity-0 pointer-events-none translate-y-4"
      }`}
    >
      <Link
        to="/contact"
        className="inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-700 text-white px-5 py-3 rounded-full shadow-2xl shadow-sky-900/30 text-sm font-semibold whitespace-nowrap border border-white/10 backdrop-blur active:scale-[0.98] transition-transform"
        data-testid="mobile-demo-pill-link"
      >
        Request a Demo
        <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  );
}
